/**
 * Dashboard Component
 *
 * This component serves as the main dashboard for the application.
 * It retrieves and displays the user's information, including their assigned terminals.
 * It also provides navigation via the Sidebar and ProfileMenu components.
 *
 * Features:
 * - Fetches user data from localStorage.
 * - Displays a list of terminals with their details.
 * - Handles user logout by clearing authentication data.
 * - Uses `useEffect` to handle menu visibility and localStorage interactions.
 *
 * State:
 * - `showProfileMenu`: Controls the visibility of the profile dropdown.
 * - `user`: Stores user details retrieved from localStorage.
 * - `terminals`: Stores the list of terminals assigned to the user.
 * - `loading`: Tracks loading state while fetching user data.
 * - `error`: Stores any error messages encountered.
 */
import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { container } from "../../infrastructure/container"
import type { Terminal, User } from "../../infrastructure/models/user"
import type { Logger } from "../../infrastructure/services/logger"
import type { SessionService } from "../../infrastructure/services/session"
import { useSearch } from "../components/ProtectedLayout"
import { MapPin } from "lucide-react"
import { COLORS } from "../constants/colors"
import { TYPES } from "../models/types"
import type { ApiService } from "../../infrastructure/services/api"
import { PAYMENT_STATUS_APPROVED } from "../constants/constant"
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"

const Dashboard: React.FC = () => {
  const sessionService = container.resolve<SessionService>("sessionService")
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")
  const { searchQuery } = useSearch()

  // const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false)
  const [user, setUser] = useState<User | null>(null)
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [totalOrders, setTotalOrders] = useState<number>(0)
  const [totalRevenue, setTotalRevenue] = useState<number>(0)

  const navigate = useNavigate()
  // const profileMenuRef = useRef<HTMLDivElement | null>(null)

  // Fetch terminals from session storage
  const fetchTerminals = useCallback(async () => {
    const storedUserDetails = sessionService.user
    if (storedUserDetails) {
      setUser(storedUserDetails)
      setTerminals(sessionService.terminals || [])

      // Extract franchiseId.
      // const franchiseId = storedUserDetails?.franchise

      // try {
      //   const page = 1
      //   const limit: number | null = null

      //   const { orders: orders } = await apiService.getOrderDetails(franchiseId, page, limit ?? 1000)
      //   setTotalOrders(orders.length)

      //   const revenue = orders.reduce((sum, order) => {
      //     const approvedPayments =
      //       order.payments?.filter((p) => p.status.toLowerCase() === PAYMENT_STATUS_APPROVED) || []
      //     const paymentTotal = approvedPayments.reduce((pSum, p) => pSum + p.amount, 0)
      //     return sum + paymentTotal
      //   }, 0)

      //   setTotalRevenue(revenue)
      // } catch (err) {
      //   logger.error("Failed to fetch order stats", err)
      // }
    } else if (sessionService.isLoggedin) {
      logger.warn("No user data found in sessionStorage.")
      setError("No user data found.")
    } else {
      window.location.href = "/login"
    }

    setLoading(false)
  }, [sessionService])

  // Fetch terminals on mount & whenever terminals update
  useEffect(() => {
    fetchTerminals()
  }, [fetchTerminals])

  /**
   * Navigates to the terminal details page.
   * @param {string} terminalId - The ID of the terminal to view.
   */
  const handleTerminalClick = (terminalId: string) => {
    navigate(`/terminal/${terminalId}`)
  }

  if (loading) {
    return <div className="text-center p-10">Loading...</div>
  }

  if (error) {
    return <div className="text-center text-red-600 p-10">{error}</div>
  }

  if (!user) {
    return <div className="text-center p-10">No user data available.</div>
  }

  // Filter terminals based on search query
  const filteredTerminals = terminals.filter(({ name, address, status }) =>
    `${name} ${address} ${status}`.toLowerCase().includes(searchQuery.toLowerCase()),
  )
  return (
    <div className="flex-1">
      <div className="flex flex-wrap items-center justify-between mb-4 px-2">
        <h2
          data-automation-id="dashboard-lists-of-terminal-text"
          id="dashboard-lists-of-terminal-text"
          className="text-2xl font-semibold text-gray-800"
        >
          List of Terminals
        </h2>
        {/* {user.role !== FranchiseRole.USER && (
          <div className="text-base text-black-1000 flex flex-col sm:flex-row sm:items-center sm:gap-4 mt-2 sm:mt-0">
            <span className="text-black-1000 font-semibold">Total Orders: {totalOrders}</span>
            <span className="text-black-1000 font-semibold">Total Revenue: ₹{totalRevenue}</span>
          </div>
        )} */}
      </div>

      {filteredTerminals.length === 0 ? (
        <p
          data-automation-id="dashboard-no-terminal-available-text"
          id="dashboard-no-terminal-available-text"
          className="text-center text-gray-500"
        >
          No terminals available.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTerminals.map(({ terminalId, name, address, status }) => (
            <div
              key={terminalId}
              onClick={() => handleTerminalClick(terminalId)}
              className="bg-white p-4 rounded-lg shadow-md text-left cursor-pointer hover:shadow-lg transition border border-gray-200 flex flex-col h-full"
            >
              <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Terminal ID:</strong> {terminalId}
              </p>
              <div className="flex items-center text-gray-500 mb-2">
                <MapPin className="w-5 h-5 text-gray-700 mr-1" />
                {/* Location icon */}
                <span className="text-sm text-gray-700">{address}</span>
                {/* Address next to the icon */}
              </div>
              <div className="mt-auto">
                <span
                  className="px-3 py-1 text-xs font-medium rounded-full text-white"
                  style={{
                    backgroundColor:
                      status.toLowerCase() === TYPES.ACTIVE
                        ? COLORS.ACTIVE
                        : status.toLowerCase() === TYPES.INACTIVE
                          ? COLORS.INACTIVE
                          : COLORS.MAINTENANCE,
                  }}
                >
                  {status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Dashboard
